import { X, FileText, BookOpen, Tag } from 'lucide-react'
import type { Document } from '../../types'
import type { JournalArticle } from '../../services/api'

interface PillDrillDownPanelProps {
  term: string
  kind: 'theme' | 'novel' | 'overlap'
  documents?: Document[]
  priorArt?: JournalArticle[]
  onClose: () => void
}

function matches(term: string, text: string): boolean {
  return text.toLowerCase().includes(term.toLowerCase())
}

function filterDocuments(term: string, documents: Document[]): Document[] {
  return documents.filter((doc) => {
    const fields = [doc.title, doc.summary, doc.description, ...(doc.topics || []), ...(doc.tags || [])]
    return fields.some((f) => f && matches(term, f))
  })
}

function filterPriorArt(term: string, priorArt: JournalArticle[]): JournalArticle[] {
  return priorArt.filter((j) => {
    const fields = [j.title, j.abstract, j.journal_name, ...(j.topics || [])]
    return fields.some((f) => f && matches(term, f))
  })
}

const KIND_LABELS: Record<PillDrillDownPanelProps['kind'], string> = {
  theme: 'Key Theme',
  novel: 'Novel Aspect',
  overlap: 'Overlap Area',
}

const KIND_COLORS: Record<PillDrillDownPanelProps['kind'], string> = {
  theme: 'bg-purple-50 text-purple-700 border-purple-200',
  novel: 'bg-teal-50 text-teal-700 border-teal-200',
  overlap: 'bg-amber-50 text-amber-700 border-amber-200',
}

export default function PillDrillDownPanel({
  term, kind, documents = [], priorArt = [], onClose,
}: PillDrillDownPanelProps) {
  const relatedDocs = filterDocuments(term, documents)
  const relatedPriorArt = filterPriorArt(term, priorArt)
  const hasResults = relatedDocs.length > 0 || relatedPriorArt.length > 0

  return (
    <div
      className="fixed inset-y-0 right-0 w-80 bg-white border-l border-sky-200 shadow-xl z-40 flex flex-col"
      data-testid="pill-drilldown-panel"
    >
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-sky-100">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 mb-1">{KIND_LABELS[kind]}</p>
          <h3 className="text-sm font-semibold text-gray-900 truncate" title={term}>{term}</h3>
        </div>
        <button
          onClick={onClose}
          className="ml-2 p-1 text-gray-500 hover:text-gray-600 rounded hover:bg-gray-100"
          aria-label="Close drill-down panel"
        >
          <X size={16} />
        </button>
      </div>

      {/* Badge */}
      <div className="px-4 pt-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${KIND_COLORS[kind]}`}>
          <Tag size={10} />
          {KIND_LABELS[kind]}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {!hasResults && (
          <div className="text-sm text-gray-500 text-center py-8">
            No documents or prior art directly mention this {KIND_LABELS[kind].toLowerCase()}.
          </div>
        )}

        {relatedDocs.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
              <FileText size={11} /> Documents ({relatedDocs.length})
            </h4>
            <ul className="space-y-2">
              {relatedDocs.map((doc) => (
                <li key={doc.document_id} className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-xs font-medium text-gray-800 truncate" title={doc.title}>{doc.title}</p>
                  {(doc.summary || doc.description) && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {(doc.summary || doc.description || '').slice(0, 100)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {relatedPriorArt.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
              <BookOpen size={11} /> Prior Art ({relatedPriorArt.length})
            </h4>
            <ul className="space-y-2">
              {relatedPriorArt.map((j) => (
                <li key={j._id} className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-xs font-medium text-gray-800 line-clamp-2" title={j.title}>{j.title}</p>
                  {j.journal_name && (
                    <p className="text-xs text-gray-500 mt-0.5 italic truncate">{j.journal_name}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  )
}
